/**
 * The per-office sync breakdown: the connector's current cycle (a big bar
 * and ETA for an office's first full import), the backfills still filling
 * (DEXIS films, document files, perio, lab cases) and what needs attention.
 * Used by the top-bar sync popover (`compact`) and Settings > Sync status.
 */
import type {
  OfficeSyncProgress,
  PmsCycle,
  SyncBackfill,
  SyncIssue,
  SyncProgress,
  SyncWorkState,
} from '@velora/contracts/sync-progress';
import { formatCount, formatEta, formatRate } from '@velora/domain/sync-progress';
import { relativeAge } from '@velora/ui';
import { CircleAlert, CircleCheck, Info, PauseCircle, TriangleAlert } from 'lucide-react';
import { useId } from 'react';
import './sync-progress.css';

const PMS_LABEL: Record<string, string> = {
  opendental: 'Open Dental',
  dentrix: 'Dentrix',
  eaglesoft: 'Eaglesoft',
};

const STATE_LABEL: Record<SyncWorkState, string> = {
  running: 'Filling',
  waiting: 'Waiting to start',
  paused: 'Paused',
  stalled: 'Stalled',
  error: 'Error',
  done: 'Complete',
  up_to_date: 'Up to date',
  unavailable: 'Not available yet',
};

/** X-ray imports read "Importing" while they run; the other backfills "Filling". */
export function backfillStateLabel(b: Pick<SyncBackfill, 'kind' | 'state'>): string {
  return b.kind === 'dexis' && b.state === 'running' ? 'Importing' : STATE_LABEL[b.state];
}

/** "in 4m", "now" (a time in the future). */
export function untilText(iso: string | null, now: number): string | null {
  if (!iso) return null;
  const sec = Math.round((Date.parse(iso) - now) / 1000);
  if (!Number.isFinite(sec)) return null;
  if (sec <= 30) return 'any moment';
  const m = Math.round(sec / 60);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  return m % 60 === 0 ? `in ${h}h` : `in ${h}h ${m % 60}m`;
}

/** "about 3 h 20 min left", "under a minute left"; null without an ETA. */
export function etaPhrase(sec: number | null): string | null {
  const e = formatEta(sec);
  if (!e) return null;
  return e === 'under a minute' ? 'under a minute left' : `about ${e} left`;
}

const CYCLE_LABEL: Record<PmsCycle['state'], string> = {
  idle: 'Idle',
  syncing: 'Syncing',
  paused: 'Paused',
  error: 'Error',
  unknown: 'No recent report',
};

/* ---------------- progress bar and ring ---------------- */

export function SyncBar({
  label,
  percent,
  valueText,
  size = 'md',
  tone = 'accent',
}: {
  label: string;
  /** 0..100, or null for an indeterminate bar (total not known yet). */
  percent: number | null;
  valueText: string;
  size?: 'md' | 'lg';
  tone?: 'accent' | 'danger' | 'muted';
}) {
  const pct = percent === null ? null : Math.max(0, Math.min(100, percent));
  return (
    <div
      className="syncp-bar"
      data-size={size}
      data-tone={tone}
      data-indeterminate={pct === null ? '' : undefined}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      {...(pct === null ? {} : { 'aria-valuenow': Math.round(pct) })}
      aria-valuetext={valueText}
    >
      <span className="syncp-bar__fill" style={pct === null ? undefined : { width: `${pct}%` }} />
    </div>
  );
}

/**
 * The small ring on the top-bar chip: a share filled, or spinning when
 * unknown. Decorative: the chip's own label says what is syncing and how far.
 */
export function SyncRing({ percent }: { percent: number | null }) {
  const r = 6;
  const c = 2 * Math.PI * r;
  const pct = percent === null ? null : Math.max(2, Math.min(100, percent));
  return (
    <span
      className="syncp-ring"
      data-indeterminate={pct === null ? '' : undefined}
      data-percent={pct === null ? undefined : Math.round(pct)}
      aria-hidden
    >
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
        <circle className="syncp-ring__track" cx="8" cy="8" r={r} />
        <circle
          className="syncp-ring__arc"
          cx="8"
          cy="8"
          r={r}
          strokeDasharray={c}
          strokeDashoffset={pct === null ? c * 0.7 : c * (1 - pct / 100)}
        />
      </svg>
    </span>
  );
}

/* ---------------- pieces ---------------- */

function stateTone(state: SyncWorkState): 'accent' | 'danger' | 'muted' {
  if (state === 'error') return 'danger';
  if (
    state === 'paused' ||
    state === 'waiting' ||
    state === 'done' ||
    state === 'up_to_date' ||
    state === 'unavailable'
  )
    return 'muted';
  return 'accent';
}

function amountLine(done: number, total: number | null, unit: string): string {
  return total === null
    ? `${formatCount(done)} ${unit} so far`
    : `${formatCount(done)} of ${formatCount(total)} ${unit}`;
}

function rateEtaLine(
  rate: number | null,
  eta: number | null,
  limit?: number | null,
): string | null {
  const parts: string[] = [];
  const r = formatRate(rate);
  if (r && rate !== null && rate > 0) parts.push(limit ? `${r} (limit ${formatRate(limit)})` : r);
  const e = etaPhrase(eta);
  if (e) parts.push(e);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function CycleBlock({
  cycle,
  office,
  officeName,
  now,
  compact,
}: {
  cycle: PmsCycle | null;
  office: OfficeSyncProgress;
  officeName: string;
  now: number;
  compact: boolean;
}) {
  const pms = office.pmsSystem ? (PMS_LABEL[office.pmsSystem] ?? office.pmsSystem) : null;
  if (!pms) return <p className="syncp-muted">No PMS linked: Velora is the system of record.</p>;
  const last = office.lastSyncedAt ? relativeAge(office.lastSyncedAt, now) : 'not yet';
  if (!cycle || cycle.state !== 'syncing') {
    const next = untilText(cycle?.nextSyncAt ?? null, now);
    const connectorLast = cycle?.lastSyncAt ? relativeAge(cycle.lastSyncAt, now) : null;
    return (
      <div className="syncp-cycle" data-state={cycle?.state ?? 'none'}>
        <p className="syncp-line">
          <span className="syncp-strong">{pms} data</span>
          <span>
            {cycle ? CYCLE_LABEL[cycle.state] : 'Live'} · Velora read {last}
          </span>
        </p>
        {connectorLast || next ? (
          <p className="syncp-muted">
            {connectorLast ? `Office cycle finished ${connectorLast}` : null}
            {connectorLast && next ? ' · ' : null}
            {next ? `next sync ${next}` : null}
          </p>
        ) : null}
      </div>
    );
  }
  const entity =
    cycle.currentEntity !== null
      ? `${cycle.currentEntity}${cycle.entityIndex !== null && cycle.totalEntities !== null ? ` (${cycle.entityIndex} of ${cycle.totalEntities})` : ''}`
      : null;
  const amount =
    cycle.recordsUploaded !== null
      ? amountLine(cycle.recordsUploaded, cycle.recordsTotal, 'records')
      : null;
  const extra = rateEtaLine(cycle.ratePerHour, cycle.etaSeconds);
  const valueText = [
    amount,
    cycle.percent !== null ? `${cycle.percent}%` : null,
    etaPhrase(cycle.etaSeconds),
  ]
    .filter(Boolean)
    .join(', ');
  if (cycle.firstImport) {
    return (
      <div className="syncp-first" data-compact={compact ? '' : undefined}>
        <p className="syncp-first__title">
          First full import from {pms}
          {cycle.percent !== null ? (
            <span className="syncp-first__pct">{cycle.percent}%</span>
          ) : null}
        </p>
        <SyncBar
          size="lg"
          label={`${officeName}: first full import`}
          percent={cycle.percent}
          valueText={valueText || 'In progress'}
        />
        <p className="syncp-line">
          <span>{amount ?? 'Counting records'}</span>
          {cycle.etaSeconds !== null ? (
            <span className="syncp-strong">{etaPhrase(cycle.etaSeconds)}</span>
          ) : null}
        </p>
        <p className="syncp-muted">
          {entity ? `Now reading ${entity}` : 'Reading'}
          {cycle.phase ? ` · ${cycle.phase}` : ''}
          {cycle.ratePerHour ? ` · ${formatRate(cycle.ratePerHour)}` : ''}
          {cycle.startedAt ? ` · started ${relativeAge(cycle.startedAt, now)}` : ''}
        </p>
      </div>
    );
  }
  return (
    <div className="syncp-cycle" data-state="syncing">
      <p className="syncp-line">
        <span className="syncp-strong">Syncing {entity ?? `${pms} data`}</span>
        {cycle.percent !== null ? <span>{cycle.percent}%</span> : null}
      </p>
      <SyncBar
        label={`${officeName}: ${pms} sync cycle`}
        percent={cycle.percent}
        valueText={valueText || 'In progress'}
      />
      <p className="syncp-muted">
        {[amount, extra, cycle.phase, `Velora read ${last}`].filter(Boolean).join(' · ')}
      </p>
    </div>
  );
}

/**
 * Whether a backfill row draws a bar. An indeterminate (unknown total) bar
 * only while the work is actually running: a caught-up, unavailable,
 * waiting or stalled row with no total reads as text, never a bar that
 * spins forever.
 */
export function backfillShowsBar(b: Pick<SyncBackfill, 'state' | 'percent'>): boolean {
  if (b.state === 'up_to_date' || b.state === 'unavailable') return false;
  if (b.state === 'done') return true;
  return b.percent !== null || b.state === 'running';
}

/** The muted line under a backfill: amounts, rate and ETA, or what it is waiting on. */
export function backfillLine(b: SyncBackfill, now: number): string {
  const unit = b.kind === 'dexis' ? 'films' : b.kind === 'documents' ? 'files' : 'rows';
  if (b.state === 'unavailable') return '';
  if (b.state === 'up_to_date') {
    const last = b.lastProgressAt
      ? `last ${b.kind === 'dexis' ? 'film' : 'item'} ${relativeAge(b.lastProgressAt, now)}`
      : null;
    return [`${formatCount(b.done)} ${unit} imported`, last].filter(Boolean).join(' · ');
  }
  const amount = amountLine(b.done, b.total, unit);
  const extra =
    b.state === 'done' ? null : rateEtaLine(b.ratePerHour, b.etaSeconds, b.perHourLimit);
  return (
    [amount, extra].filter(Boolean).join(' · ') +
    (b.state === 'stalled' && b.lastProgressAt
      ? ` · last progress ${relativeAge(b.lastProgressAt, now)}`
      : '')
  );
}

function BackfillRow({ b, officeName, now }: { b: SyncBackfill; officeName: string; now: number }) {
  const line = backfillLine(b, now);
  const label = backfillStateLabel(b);
  return (
    <li className="syncp-fill" data-state={b.state}>
      <p className="syncp-line">
        <span className="syncp-strong">{b.label}</span>
        <span className="syncp-state" data-state={b.state}>
          {label}
          {b.percent !== null && b.state !== 'done' && b.state !== 'up_to_date'
            ? ` · ${b.percent}%`
            : ''}
        </span>
      </p>
      {backfillShowsBar(b) ? (
        <SyncBar
          label={`${officeName}: ${b.label}`}
          percent={b.state === 'done' ? 100 : b.percent}
          tone={stateTone(b.state)}
          valueText={[line, b.percent !== null ? `${b.percent}%` : null, label]
            .filter(Boolean)
            .join(', ')}
        />
      ) : null}
      {line ? <p className="syncp-muted">{line}</p> : null}
      {b.detail ? <p className="syncp-muted">{b.detail}</p> : null}
    </li>
  );
}

function IssueIcon({ issue }: { issue: SyncIssue }) {
  if (issue.level === 'error') return <CircleAlert size={14} aria-hidden />;
  if (issue.code === 'paused') return <PauseCircle size={14} aria-hidden />;
  if (issue.level === 'warning') return <TriangleAlert size={14} aria-hidden />;
  return <Info size={14} aria-hidden />;
}

function OfficeBlock({
  office,
  name,
  now,
  compact,
  showName,
}: {
  office: OfficeSyncProgress;
  name: string;
  now: number;
  compact: boolean;
  showName: boolean;
}) {
  const headingId = useId();
  const issues = compact ? office.issues.filter((i) => i.level !== 'info') : office.issues;
  return (
    <section
      className="syncp-office"
      aria-labelledby={showName ? headingId : undefined}
      aria-label={showName ? undefined : name}
      data-active={office.active ? '' : undefined}
    >
      {showName ? (
        <header className="syncp-office__head">
          <h3 id={headingId}>{name}</h3>
          <span className="syncp-muted">
            {office.pmsSystem
              ? `${PMS_LABEL[office.pmsSystem] ?? office.pmsSystem} via PulseSync`
              : 'No PMS'}
            {office.connector !== 'unknown' && office.pmsSystem
              ? ` · connector ${office.connector}`
              : ''}
          </span>
        </header>
      ) : null}
      <CycleBlock
        cycle={office.cycle}
        office={office}
        officeName={name}
        now={now}
        compact={compact}
      />
      {office.backfills.length > 0 ? (
        <ul className="syncp-fills" aria-label={`${name}: backfills`}>
          {office.backfills.map((b) => (
            <BackfillRow key={b.kind} b={b} officeName={name} now={now} />
          ))}
        </ul>
      ) : null}
      {issues.length > 0 ? (
        <ul className="syncp-issues" aria-label={`${name}: needs attention`}>
          {issues.map((i, n) => (
            <li key={`${i.code}-${n}`} className="syncp-issue" data-level={i.level}>
              <IssueIcon issue={i} />
              <span>{i.text}</span>
            </li>
          ))}
        </ul>
      ) : office.pmsSystem || office.backfills.length > 0 ? (
        <p className="syncp-ok">
          <CircleCheck size={14} aria-hidden /> Nothing needs attention
        </p>
      ) : null}
    </section>
  );
}

/** Summary line for the chip and the popover heading. */
export function summaryText(data: SyncProgress): string {
  const syncing = data.offices.filter((o) => o.cycle?.state === 'syncing').length;
  const filling = data.offices.reduce(
    (n, o) => n + o.backfills.filter((b) => b.state === 'running').length,
    0,
  );
  const parts: string[] = [];
  if (syncing > 0) parts.push(`${syncing} office${syncing > 1 ? 's' : ''} syncing`);
  if (filling > 0) parts.push(`${filling} backfill${filling > 1 ? 's' : ''} running`);
  const attention = data.offices.reduce(
    (n, o) => n + o.issues.filter((i) => i.level !== 'info').length,
    0,
  );
  const head = parts.length === 0 ? 'Nothing syncing right now' : parts.join(', ');
  return attention > 0 ? `${head}; ${attention} need${attention === 1 ? 's' : ''} attention` : head;
}

export function SyncProgressView({
  data,
  names,
  now,
  compact = false,
}: {
  data: SyncProgress;
  /** Office id to display name. */
  names: ReadonlyMap<string, string>;
  now: number;
  compact?: boolean;
}) {
  const shown = compact
    ? data.offices.filter((o) => o.pmsSystem !== null || o.backfills.length > 0)
    : data.offices;
  const showName = data.offices.length > 1 || !compact;
  if (shown.length === 0) {
    return <p className="syncp-muted">No office in this view syncs from a PMS or imports data.</p>;
  }
  return (
    <div className="syncp-view" data-compact={compact ? '' : undefined}>
      {shown.map((o) => (
        <OfficeBlock
          key={o.locationId}
          office={o}
          name={names.get(o.locationId) ?? 'Office'}
          now={now}
          compact={compact}
          showName={showName}
        />
      ))}
    </div>
  );
}
